/* eslint-disable react-hooks/exhaustive-deps */
import { Form, Formik } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import Container from '@material-ui/core/Container';
import Hidden from '@material-ui/core/Hidden';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import AppLogo from '../../components/app-logo';
import ConfirmInput from '../../components/confirm-input';
import CookieConsentConfirmationButton from '../../components/cookie-consent';
import DisplayErrorMessage from '../../components/display-error-message';
import SplashWait from '../../components/splash-wait';
import TermsConditions from '../../components/terms-conditions';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import {
  LoginBackgroundImage, VisibilityIcon, VisibilityOffIcon, WarningIcon
} from '../../utils/icons';
import {
  Box, Button, FormControlLabel, Grid, LinearProgress, Link, Switch, TextField, Typography
} from '../../utils/material-ui-core';

const loginValidationSchema = Yup.object().shape({
  email: Yup.string().email("Email is not valid.").required("Email is required."),
  password: Yup.string().min(8, "Password is too short.").max(20, "Password is too long.").required("Password is required."),
});

interface IFormValues {
  email: string;
  password: string;
}

const initialFormValues = {
  email: "",
  password: "",
};

const Login = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { isUserAuthenticated, validateLoginUser, checkAutoLogin, dataError, setAppReady } = useContext(AppContext);

  const formReference = useRef(null) as any;

  const [cookieEnabled, setCookieEnabled] = useState<boolean>(navigator.cookieEnabled);
  const [loading, setLoading] = useState<boolean>(true);
  const [signin, setSignin] = useState<boolean>(true);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [forgotPasswordDialogOpen, setForgotPasswordDialogOpen] = useState<boolean>(false);

  const openForgotPasswordDialog = () => {
    setForgotPasswordDialogOpen(true);
  };

  const resetPassword = (confirmOutput: ConfirmOutput) => {
    setForgotPasswordDialogOpen(false);

    if (confirmOutput.status === "save") {
      //TODO: Save Code
    }
  };

  const handleSubmitLoginForm = async (values: IFormValues, actions: any) => {
    setSignin(true);
    const confirm = await validateLoginUser(values.email, values.password, rememberMe);
    if (Boolean(confirm)) {
      if (!isUserAuthenticated) {
        actions.setSubmitting(false);
      }
    }
    setSignin(false);
    return false;
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const submitButtonEnable = (errors: any, isSubmitting: boolean) => {
    if (isSubmitting) {
      return true;
    } else if (isEmpty(errors)) {
      return false;
    }
    return true;
  };

  const isEmpty = (obj: any) => {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) return false;
    }
    return true;
  };

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [isUserAuthenticated]);

  useEffect(() => {
    const initialPageAsync = async () => {

      setCookieEnabled(navigator.cookieEnabled);

      if (!cookieEnabled) {
        setLoading(false);
        return;
      }

      if (!loading) {
        if (!isUserAuthenticated) {
          await Promise.all([checkAutoLogin()]).then((res) => {
            const confirm: boolean = res[0];
            if (!confirm && !isMounted) {
              setSignin(false);
            }
          });
        } else if (isUserAuthenticated) {
          setIsMounted(true);
          setTimeout(() => {
            setAppReady(true);
          }, 5000);
        }
      }
    };

    initialPageAsync();
  }, [isUserAuthenticated, loading]);

  return (
    <React.Fragment>
      {loading && isMounted ? (
        <SplashWait />
      ) : (
        <Box p={0}>
          <CookieConsentConfirmationButton />

          <ConfirmInput title="Reset Password" label="Email" content={"Enter the email address associated with your account, and we'll email you a link to reset your password."} value={""} type={"email"} required={true} yesText="RESET" noText="CANCEL" open={forgotPasswordDialogOpen} callbackOnSave={resetPassword} deleteConfirmationText={""} showDeleteButton={false} />

          <Grid container spacing={0} className={pageStyles.gridContainer}>
            <Grid item xs={12} sm={7} md={6} lg={4}>

              <Box height="80vh" display="flex" alignItems="center">
                {
                  !cookieEnabled ?
                    <Box p={2}>
                      <Box display="flex" p={2} justifyContent="center" alignContent="center">
                        <AppLogo />
                      </Box>
                      <Box m="auto" display="flex" flexDirection="column" p={2} justifyContent="center" alignItems="center">
                        <Box p={2}><WarningIcon fontSize='large' className={globalStyles.error} /></Box>

                        <Typography variant='h5'>
                          COOKIES ARE DISABLED
                        </Typography>


                        <Box p={1}>&nbsp;</Box>
                        <Typography variant='h6'>
                          Oops! Your browser seems to have cookies disabled. Make sure cookies are enabled or try opening a new browser window.
                        </Typography>
                      </Box>
                    </Box>
                    :
                    <Container maxWidth="xs">
                      <Box p={2}>
                        <Box display="flex" p={2} justifyContent="center" alignContent="center">
                          <AppLogo />
                        </Box>
                        {signin || isUserAuthenticated ? (
                          <Box m="auto" display="flex" p={2} justifyContent="center" alignItems="center" className={pageStyles.signinMessage}>
                            <Box m="auto" display="flex" p={2} justifyContent="center">
                              <Box p={2} style={{ letterSpacing: 4 }}>
                                <Typography variant="caption">CONNECTING</Typography>
                                <LinearProgress color="secondary" />
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          <Formik innerRef={formReference} initialValues={initialFormValues} validationSchema={loginValidationSchema} validateOnBlur={true} validateOnChange={true} onSubmit={handleSubmitLoginForm}>
                            {({ values, errors, handleChange, touched, handleBlur, isSubmitting, setSubmitting }) => (
                              <Form
                                onSubmit={(e) => {
                                  e.preventDefault();
                                  handleSubmitLoginForm(values, { setSubmitting });
                                }}
                              >
                                <Grid container spacing={3}>
                                  <Grid item xs={12}>
                                    <Typography variant="body2">Log in with your email &amp; password used to create your account.</Typography>
                                  </Grid>
                                  {dataError ? (
                                    <Grid item xs={12} className={pageStyles.errorMessage}>
                                      <DisplayErrorMessage message={dataError} />
                                    </Grid>
                                  ) : null}

                                  <Grid item xs={12}>
                                    <Grid container>
                                      <Grid item xs={6} className={pageStyles.inputLabelGridRow}>
                                        <Typography variant="caption" color="textSecondary">
                                          Email
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                    <TextField
                                      id="email"
                                      name="email"
                                      type="email"
                                      value={values.email}
                                      autoComplete={"on"}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      fullWidth={true}
                                      InputLabelProps={{
                                        shrink: true,
                                      }}
                                      error={touched.email && Boolean(errors.email)}
                                      helperText={errors.email && touched.email ? errors.email : null}
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <Grid container>
                                      <Grid item xs={6} className={pageStyles.inputLabelGridRow}>
                                        <Typography variant="caption" color="textSecondary">
                                          Password
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={6} className={pageStyles.inputLabelGridRow}>
                                        <Box width={1} display="flex" flexDirection="row-reverse">
                                          <Link
                                            href=""
                                            onClick={(e: React.SyntheticEvent) => {
                                              e.preventDefault();
                                              handleClickShowPassword();
                                            }}
                                            color={!showPassword ? "textSecondary" : "secondary"}
                                          >
                                            <Box width={1} display="flex" justifyItems="flex-end">
                                              <Box component="div">{!showPassword ? <VisibilityIcon fontSize="medium" /> : <VisibilityOffIcon fontSize="medium" />}</Box>
                                              <Box component="div" className={pageStyles.showHideText}>
                                                {!showPassword ? " Show" : " Hide"}
                                              </Box>
                                            </Box>
                                          </Link>
                                        </Box>
                                      </Grid>
                                    </Grid>

                                    <TextField
                                      id="password"
                                      label=""
                                      type={showPassword ? "text" : "password"}
                                      onChange={handleChange}
                                      onKeyPress={(event) => {
                                        if (event.key === "Enter") {
                                          formReference.current.handleSubmit();
                                        }
                                      }}
                                      onBlur={handleBlur}
                                      autoComplete="current-password"
                                      fullWidth={true}
                                      InputLabelProps={{
                                        shrink: true,
                                      }}
                                      error={touched.password && Boolean(errors.password)}
                                      helperText={errors.password && touched.password ? errors.password : null}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <FormControlLabel control={<Switch color="secondary" checked={rememberMe} onChange={() => setRememberMe(!rememberMe)} />} label="Remember Me " />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Button disabled={submitButtonEnable(errors, isSubmitting)} id="login-button-login" variant="contained" color="primary" onClick={() => formReference.current.handleSubmit()} fullWidth={true} className={globalStyles.buttonDefault}>
                                      LOG IN
                                    </Button>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Button id="login-button-forgot-password" color="primary" fullWidth={true} onClick={openForgotPasswordDialog} className={globalStyles.buttonDefault}>
                                      Forgot Password?
                                    </Button>
                                  </Grid>
                                </Grid>
                              </Form>
                            )}
                          </Formik>
                        )}
                      </Box>
                    </Container>
                }
              </Box>
              <Box height="13vh" display="flex" alignItems="center">
                <Box p={2} display="flex" style={{ position: "absolute", bottom: 0 }}>
                  <TermsConditions />
                </Box>
              </Box>
            </Grid>

            <Hidden xsDown>
              <Grid item xs={12} sm={5} md={6} lg={8} className={pageStyles.cardMediaRoot}></Grid>
            </Hidden>
          </Grid>
        </Box>
      )}
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridContainer: {
      height: "100vh",
    },
    cookieConsentConfirmationButton: {
      background: theme.palette.text.primary,
      color: "#ffffff",
      height: "100%",
    },
    cardMediaRoot: {
      height: "100%",
      width: "100%",
      backgroundImage: `url(${LoginBackgroundImage})`,
      backgroundPosition: "center center",
      backgroundSize: "cover",
    },
    showHideText: {
      paddingLeft: 5,
      fontWeight: "revert",
    },
    inputLabelGridRow: {
      height: "30px",
    },
    errorMessage: {
      "& ::first-letter": {
        textTransform: "uppercase",
      },
    },
    signinMessage: {
      height: "200px",
    },
  })
);

export default Login;
